/**
 * resolveTabsFromCartAndCatalogue — single source of truth for the
 * `(cartItemId, flowId)` tuple list that drives the configuration step tabs,
 * chip-strip badges, and the step-valid selector.
 *
 * Extracted from three call sites that each derived the list independently:
 *   - `tabs` useMemo in `ProductConfiguration.page.tsx`
 *   - `chipItems` in the same file
 *   - `selectConfigurationStepValid` in `selectors.ts`
 *
 * Previously the page used `resolveShellFlows` (which applies the FE fallback
 * resolver when `allowFallback` is true), while the selector read
 * `shell?.configurationFlows ?? []` directly (no fallback). This divergence
 * meant a product whose `configurationFlows` is empty but resolved by the FE
 * fallback would show tabs in the UI yet have `selectConfigurationStepValid`
 * return `true` immediately (zero tuples → always valid), breaking the Proceed
 * gate for fallback-resolved flows.
 *
 * Both consumers now call this function with the same `allowFallback` value so
 * they always agree on the tab set.
 */

import type { ConfigurationFlowId } from 'models/purchaseFlowRevamp'
import { NESTED_FLOWS, resolveConfigurationFlows } from 'utils/purchaseFlowRevamp'

import { shootOutputLines } from '../../store/shootOutput'

/** Minimal shape from the cart slice — avoids importing the full slice type. */
export interface CartItemLike {
  id: string
  shellProductId: string
  /** The variant actually bought. Decides the flows whenever the shell's
   *  variants disagree on `requiredConfiguration`. */
  variantId?: string
  /** The drawer's add-on toggles, keyed by option id. */
  optionValues?: Record<string, unknown>
  /** Set on a line that belongs to a pack. */
  pack?: unknown
}

/** Minimal shape from the catalogue response. */
export interface ShellProductLike {
  id: string
  configurationFlows?: ConfigurationFlowId[]
  productType?: string
  kind?: string
  productGroupCode?: string
  variants?: Array<{
    id: string
    kind?: string
    configurationFlows?: ConfigurationFlowId[]
    options?: Array<{ id: string; configurationFlows?: ConfigurationFlowId[] }>
  }>
}

const isTicked = (value: unknown): boolean =>
  value === true || (typeof value === 'number' && value > 0) || (typeof value === 'string' && Number(value) > 0)

/**
 * The configuration steps the line's ticked add-ons need at order time, in option order. They share
 * the line's config keys: the backend allows each configuration type once per line, so an add-on's
 * answer and the line's own can never be of the same type.
 */
export function addonConfigurationFlows(
  item: CartItemLike,
  shell: ShellProductLike | null
): ConfigurationFlowId[] {
  const variant = shell?.variants?.find((v) => v.id === item.variantId)
  const flows: ConfigurationFlowId[] = []
  for (const option of variant?.options ?? []) {
    if (!isTicked(item.optionValues?.[option.id])) continue
    for (const flow of option.configurationFlows ?? []) {
      if (!NESTED_FLOWS.has(flow) && !flows.includes(flow)) flows.push(flow)
    }
  }
  return flows
}

export { NESTED_FLOWS }

/**
 * Flows whose answer belongs to the ORDER rather than to one line, so they are
 * collected ONCE however many lines need them.
 *
 * The authorities-document form is the clearest case: the property information
 * and the power of attorney describe the property and the client, not the
 * document — so three documents asked the same questions three times and made
 * the client save each copy to clear the Proceed gate.
 *
 * Floor plans also share one configuration but are NOT here: each floor-plan line
 * needs its own tab for its own source plan, so the module writes the shared answer
 * to every floor-plan line instead (`useFloorPlanConfigDraft`).
 *
 * `property_info` is here although it is never a tab of its own (no
 * `requiredConfiguration` maps to it): it is collected on the power-of-attorney
 * page and stored under its own key, so placement has to look it up in the same
 * place the client filled it in.
 *
 * Which flows these are is a product decision and is expected to change —
 * extend the set, and both the tab list and placement follow.
 */
export const ORDER_LEVEL_FLOWS: ReadonlySet<ConfigurationFlowId> = new Set<ConfigurationFlowId>([
  'power_of_attorney',
  'property_info',
])

/**
 * Flows whose single answer every line of the flow is placed with: the order-level ones, plus the
 * floor-plan configuration, which each floor-plan line also stores itself.
 */
export const SHARED_ANSWER_FLOWS: ReadonlySet<ConfigurationFlowId> = new Set<ConfigurationFlowId>([
  ...ORDER_LEVEL_FLOWS,
  'floor_plan_config',
])

/**
 * Which cart line holds each order-level flow's single answer: the FIRST line
 * that needs it, in display order, so the tab keeps a stable home as long as
 * that line is in the cart.
 *
 * Shared by the configuration step and by placement, so the line the client
 * filled in and the lines that are serialized from it cannot disagree.
 */
export function orderLevelConfigOwners(
  items: CartItemLike[],
  shellProducts: ShellProductLike[],
  allowFallback: boolean
): Map<ConfigurationFlowId, string> {
  const owners = new Map<ConfigurationFlowId, string>()
  for (const item of items) {
    const shell = shellProducts.find((s) => s.id === item.shellProductId) ?? null
    for (const flow of resolveShellFlows(shell, allowFallback, item.variantId)) {
      if (ORDER_LEVEL_FLOWS.has(flow) && !owners.has(flow)) owners.set(flow, item.id)
    }
  }
  return owners
}

export interface ConfigTab {
  cartItemId: string
  flowId: ConfigurationFlowId
  /** Index of this cart item among items that have at least one config flow. */
  itemIndex: number
  /** Index of this flow among the owning item's resolved flows. */
  flowIndex: number
  /** Total number of flows on the owning cart item — drives tab label suffix. */
  flowCountOnItem: number
}

/**
 * Derives the ordered `(cartItemId, flowId)` tuples from the cart and
 * catalogue data. Returns an empty array until the catalogue is loaded.
 *
 * @param items          - Cart items in display order.
 * @param shellProducts  - The catalogue shell products array.
 * @param allowFallback  - When `true`, applies the FE-side `(productType|kind)`
 *                         fallback resolver for shells that have an empty
 *                         `configurationFlows` array from the BE. Must be the
 *                         same value passed to both the page render and the
 *                         validity selector so they agree on the tab list.
 */
export function resolveTabsFromCartAndCatalogue(
  items: CartItemLike[],
  shellProducts: ShellProductLike[],
  allowFallback: boolean
): ConfigTab[] {
  const out: ConfigTab[] = []
  let itemIndex = 0
  const owners = orderLevelConfigOwners(items, shellProducts, allowFallback)
  // Made from the shoot's photos: nothing to configure while ordering.
  const fromShoot = shootOutputLines(items, shellProducts)

  for (const item of items) {
    if (fromShoot.has(item.id)) continue
    const shell = shellProducts.find((s) => s.id === item.shellProductId) ?? null
    // An order-level flow appears only on the line that owns it; on every other
    // line it is already answered, so a second tab would ask again.
    const ownFlows = resolveShellFlows(shell, allowFallback, item.variantId)
      .filter((flow) => !ORDER_LEVEL_FLOWS.has(flow) || owners.get(flow) === item.id)
    const flows = [...ownFlows, ...addonConfigurationFlows(item, shell).filter((flow) => !ownFlows.includes(flow))]
    if (flows.length === 0) continue

    flows.forEach((flow, flowIndex) => {
      out.push({
        cartItemId: item.id,
        flowId: flow,
        itemIndex,
        flowIndex,
        flowCountOnItem: flows.length,
      })
    })
    itemIndex += 1
  }

  return out
}

/**
 * Internal helper — resolves the effective `ConfigurationFlowId[]` for a cart
 * line, applying the FE fallback when `allowFallback` is true and nothing else
 * yields flows, and dropping the flows the drawer collects.
 *
 * The SELECTED VARIANT wins over the shell. A shell's `configurationFlows` is
 * the union across its variants, and the dev catalogue's ENERGY_CERTIFICATE
 * group holds two products with different `requiredConfiguration` — so the union
 * put both certificate forms on the step for a line that is only ever one of
 * them, and the step's gate demands every tuple be confirmed. Falling back to
 * the union when the variant is unknown (a draft naming a retired product, a
 * shell whose variants carry no flows of their own) keeps a configurable line
 * configurable rather than silently dropping its step.
 */
function resolveShellFlows(
  shell: ShellProductLike | null | undefined,
  allowFallback: boolean,
  variantId?: string
): ConfigurationFlowId[] {
  if (!shell) return []
  const variant = variantId ? shell.variants?.find((v) => v.id === variantId) : undefined
  const variantFlows = variant?.configurationFlows ?? []
  const flows = variantFlows.length > 0
    ? variantFlows
    : shell.configurationFlows && shell.configurationFlows.length > 0
      ? shell.configurationFlows
      : allowFallback
        ? resolveConfigurationFlows(shell.productType ?? '', shell.kind)
        : []
  return flows.filter((flow) => !NESTED_FLOWS.has(flow))
}
